import * as THREE from 'three';

/**
 * Seagulls over the coast (the island alive, Pieter 28 Sep 2026): five birds
 * of six triangles, drawn half as big again as life, one instanced mesh, gliding in slow circles 15–40 m up
 * over the sea and the volcano's foot -- never over the stage. Their wings
 * beat now and then in the vertex shader; the CPU moves five matrices a frame.
 */

const euler = new THREE.Euler();

export type GullFlight = {
  /** Where the circle's middle is (x, z), how wide it is, and how high. */
  readonly centre: readonly [number, number];
  readonly radius: number;
  readonly height: number;
  /** Metres a second along the circle; negative turns the other way. */
  readonly speed: number;
  readonly phase: number;
};

/** Circles over the sea ahead, the shore to the left and right, and the volcano's foot. */
export const GULL_FLIGHTS: readonly GullFlight[] = [
  // Close enough over the water ahead to be seen as birds: 30–40 m out at the nearest.
  { centre: [0, -48], radius: 16, height: 18, speed: 5.5, phase: 0 },
  { centre: [-42, -36], radius: 15, height: 22, speed: -5, phase: 2.1 },
  { centre: [48, -22], radius: 16, height: 18.5, speed: 5.5, phase: 4.2 },
  { centre: [165, -105], radius: 40, height: 34, speed: -7.5, phase: 1.3 },
  { centre: [-28, -118], radius: 20, height: 26, speed: 5.5, phase: 3.3 },
];

/** Where a gull on `flight` is at `t` seconds, turned to fly on and banked into the turn. */
export function gullAt(
  flight: GullFlight,
  t: number,
  into = { position: new THREE.Vector3(), turn: new THREE.Quaternion() },
): { position: THREE.Vector3; turn: THREE.Quaternion } {
  const w = flight.speed / flight.radius;
  const a = flight.phase + w * t;
  const position = into.position.set(
    flight.centre[0] + Math.cos(a) * flight.radius,
    flight.height + 2 * Math.sin(t * 0.3 + flight.phase),
    flight.centre[1] + Math.sin(a) * flight.radius,
  );
  // The way it goes: the circle's tangent.
  const vx = -Math.sin(a) * Math.sign(w);
  const vz = Math.cos(a) * Math.sign(w);
  const yaw = Math.atan2(-vx, -vz);
  const bank = 0.3 * Math.sign(w);
  const turn = into.turn.setFromEuler(euler.set(0, yaw, bank, 'YXZ'));
  return { position, turn };
}

/**
 * A gull seen from below and far off: a body and two bent wings, nose to −z,
 * 1.8 m across. `wing` is 0 at the body, 1 at the tips: how much a vertex
 * moves when it beats.
 */
export function gullGeometry(): THREE.BufferGeometry {
  // prettier-ignore
  const positions = [
    0, 0, -0.35, -0.08, 0, 0, 0, 0, 0.35, 0.08, 0, 0,        // body: nose, left, tail, right
    -0.08, 0, -0.1, -0.08, 0, 0.12, -0.45, 0.08, 0, -0.9, 0, 0.16, // left wing: root f/b, elbow, tip
    0.08, 0, -0.1, 0.08, 0, 0.12, 0.45, 0.08, 0, 0.9, 0, 0.16,     // right wing
  ];
  const wing = [0, 0, 0, 0, 0, 0, 0.5, 1, 0, 0, 0.5, 1];
  const index = [0, 1, 2, 0, 2, 3, 4, 5, 6, 6, 5, 7, 8, 10, 9, 10, 11, 9];
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('wing', new THREE.Float32BufferAttribute(wing, 1));
  geometry.setIndex(index);
  geometry.computeVertexNormals();
  return geometry;
}

/**
 * The gulls, one instanced mesh, and how to move them on `elapsed` seconds.
 * Other birds on other flights -- a square's pigeons (part 2) -- take their
 * own flights, size and colours.
 */
export function buildGulls(
  clock: THREE.IUniform<number>,
  options: {
    flights?: readonly GullFlight[];
    size?: number;
    colour?: THREE.ColorRepresentation;
    emissive?: THREE.ColorRepresentation;
    name?: string;
  } = {},
): {
  mesh: THREE.InstancedMesh;
  update(elapsed: number): void;
} {
  const flights = options.flights ?? GULL_FLIGHTS;
  // White from below too: seen against the sky, a gull's underside is lit by the sea and the sky.
  const material = new THREE.MeshLambertMaterial({
    color: options.colour ?? 0xf4f3ee,
    emissive: options.emissive ?? 0x6e6e6a,
    side: THREE.DoubleSide,
  });
  material.onBeforeCompile = (shader) => {
    shader.uniforms['uClock'] = clock;
    shader.vertexShader =
      'attribute float wing;\nuniform float uClock;\n' +
      shader.vertexShader.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        float gullPhase = float(gl_InstanceID) * 2.1;
        // Mostly gliding; now and then a few beats.
        float beating = smoothstep(0.3, 0.9, sin(uClock * 0.55 + gullPhase * 1.7));
        float beat = sin(uClock * 7.0 + gullPhase) * beating;
        transformed.y += wing * wing * 0.45 * beat + wing * 0.06;`,
      );
  };
  material.customProgramCacheKey = () => 'gull';
  const mesh = new THREE.InstancedMesh(gullGeometry(), material, flights.length);
  mesh.name = options.name ?? 'seagulls';
  mesh.frustumCulled = false;
  mesh.castShadow = false;
  const matrix = new THREE.Matrix4();
  // Over life size (2.7 m across), so a gull 40 m off still reads as a bird in a headset.
  const unit = new THREE.Vector3().setScalar(options.size ?? 1.5);
  // Reused every frame: nothing for the garbage collector.
  const at = { position: new THREE.Vector3(), turn: new THREE.Quaternion() };
  const update = (elapsed: number): void => {
    flights.forEach((flight, i) => {
      gullAt(flight, elapsed, at);
      mesh.setMatrixAt(i, matrix.compose(at.position, at.turn, unit));
    });
    mesh.instanceMatrix.needsUpdate = true;
  };
  update(0);
  return { mesh, update };
}
